import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../ui/card'
import { Label } from '../../../ui/label'
import { Input } from '../../../ui/input'
import { FileUpload } from '../../file-upload'
import { Download, AlertCircle } from 'lucide-react'
import { cn } from '../../../../lib/utils'

interface OverviewDocumentsSectionProps {
  localData: {
    pitchDeck: File | null
    researchPath: string
    researchReport: string
  }
  errors: Record<string, string>
  setErrors: (
    updater: (prev: Record<string, string>) => Record<string, string>,
  ) => void
  deckUploading: boolean
  deckUploadError?: string
  onPitchDeckSelected: (file: File | null) => void
  updateField: (field: string, value: any) => void
  toggleResearchPath: (value: 'link' | 'outsourced') => void
  requireCoreDocs: boolean
  requestChanges?: Record<string, string>
}

export function OverviewDocumentsSection({
  localData,
  errors,
  setErrors,
  deckUploading,
  deckUploadError,
  onPitchDeckSelected,
  updateField,
  toggleResearchPath,
  requireCoreDocs,
  requestChanges,
}: OverviewDocumentsSectionProps) {
  return (
    <Card id="documents">
      <CardHeader>
        <CardTitle>
          Documents
          {requireCoreDocs ? '' : ' (optional)'}
        </CardTitle>
        <CardDescription className="text-sm font-normal text-neutral-600 mt-1">
          Upload your pitch deck as a PDF and add your research report link
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-10 mb-0">
        <div className="space-y-2" id="pitchDeck">
          <Label>Pitch Deck (PDF)</Label>
          <FileUpload
            mode="document"
            accept="application/pdf"
            buttonLabel="Upload PDF"
            helperText="PDF only • Max 25 MB • No Docsend links"
            maxSizeMB={25}
            errorStrings={{
              fileCount: 'Please upload one file.',
              fileType: 'PDF only. Please upload a .pdf file.',
              fileSize: 'File is too large (max 25 MB).',
              unreadable: "We couldn't read this PDF. Please try another file.",
              password:
                'This PDF is password protected. Please upload an unlocked file.',
              empty: 'The file appears to be empty.',
              generic: 'Upload failed. Please try again.',
              linkDropNotAllowed:
                'Links are not accepted. Please upload a PDF file.',
            }}
            ariaLabel="Upload pitch deck PDF file"
            onFileSelect={onPitchDeckSelected}
            externalUploading={deckUploading}
            externalError={deckUploadError}
            currentFile={localData.pitchDeck}
            error={errors.pitchDeck}
          />
          <div className="flex flex-col gap-1">
            {requestChanges?.pitchDeck && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.pitchDeck}
              </p>
            )}
          </div>
        </div>
        <div className="border-t border-neutral-200"></div>

        <fieldset className="space-y-4 mt-6">
          <legend className="text-base font-medium text-neutral-800 mb-0">
            Research Materials
          </legend>
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="flex items-start gap-3">
                <button
                  type="button"
                  role="radio"
                  aria-checked={localData.researchPath === 'link'}
                  onClick={() => toggleResearchPath('link')}
                  className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center transition-colors ${
                    localData.researchPath === 'link'
                      ? 'border-brand-primary bg-brand-primary'
                      : 'border-neutral-300 hover:border-brand-primary'
                  }`}
                >
                  {localData.researchPath === 'link' && (
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  )}
                </button>
                <div className="leading-none">
                  <span className="text-sm font-normal text-neutral-800">
                    Provide a research report link
                  </span>
                  <p
                    id="researchReport-helper"
                    className="text-xs text-neutral-500 -mt-1 pt-1"
                  >
                    Paste a public URL
                  </p>
                </div>
              </label>
              {localData.researchPath === 'link' && (
                <div className="ml-6 space-y-1">
                  <Input
                    id="researchReport"
                    value={localData.researchReport}
                    onChange={(e) =>
                      updateField('researchReport', e.target.value)
                    }
                    onBlur={(e) => {
                      const value = e.target.value
                      if (value && !value.startsWith('https://'))
                        setErrors((prev) => ({
                          ...prev,
                          researchReport: 'Enter a valid URL (https).',
                        }))
                      else if (value && value.length > 2000)
                        setErrors((prev) => ({
                          ...prev,
                          researchReport: 'This URL is too long.',
                        }))
                      else
                        setErrors((prev) => ({ ...prev, researchReport: '' }))
                    }}
                    placeholder="https://"
                    className={cn(
                      'focus:ring-0 focus:border-neutral-600 border-neutral-200 hover:border-neutral-600 placeholder:text-neutral-400',
                      errors.researchReport &&
                        'border-status-error border bg-status-errorBG',
                    )}
                    aria-describedby={
                      errors.researchReport ? 'researchReport-error' : undefined
                    }
                  />
                  <div className="flex flex-col gap-1">
                    {errors.researchReport && (
                      <p
                        id="researchReport-error"
                        role="alert"
                        aria-live="polite"
                        className="text-xs text-status-error inline-flex items-center gap-1"
                      >
                        <AlertCircle className="w-3 h-3" />
                        {errors.researchReport}
                      </p>
                    )}
                    {requestChanges?.researchReport && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.researchReport}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500">
                    Optional: Use the{' '}
                    <a
                      href="https://www.notion.so/impossiblefinance/Template-Research-Report-2407cf9e26b88090aafedb9e4ee38574"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-primary hover:text-brand-hover underline inline-flex items-center gap-1"
                    >
                      IF Research Report template
                      <Download className="w-3 h-3" />
                    </a>
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-3">
                <button
                  type="button"
                  role="radio"
                  aria-checked={localData.researchPath === 'outsourced'}
                  onClick={() => toggleResearchPath('outsourced')}
                  className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center transition-colors ${
                    localData.researchPath === 'outsourced'
                      ? 'border-brand-primary bg-brand-primary'
                      : 'border-neutral-300 hover:border-brand-primary'
                  }`}
                >
                  {localData.researchPath === 'outsourced' && (
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  )}
                </button>
                <div className="leading-none">
                  <span className="text-sm font-normal text-neutral-800">
                    Request research support from the IF Research Team
                  </span>
                  <p className="text-xs font-normal text-neutral-500 -mt-1 pt-1">
                    Our team can prepare a research report for this project.
                    We'll contact you after submission
                  </p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {errors.researchReport && (
              <p className="text-xs text-status-error inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.researchReport}
              </p>
            )}
            {requestChanges?.researchReport && (
              <p className="text-xs text-status-error inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.researchReport}
              </p>
            )}
          </div>
        </fieldset>
      </CardContent>
    </Card>
  )
}
